/**
 * ruleConstants.ts
 *
 * This file contains the enumeration constants used for scale rules.
 * These enums define the types of rules, input value references, and conditions
 * that can be applied in data visualization scaling operations.
 */

// #####################################################################
// ENUMS:
// #####################################################################

/**
 * Enum for domain value reference types.
 *
 *  Defines the different ways to reference values within a domain for scaling operations.
 */
export enum ScaleInputValueType {
  Exact = "exact", // Fixed/final/absolute value -> postfix = "" (none)

  // Relative measures:
  Fraction = "frac", // Fractional value (0-1) -> postfix = "frac" (after input)
  Percentile = "pn", // Percentile value (0-100) -> postfix = "pn" (after input)
  BottomX = "bottom_x", // Bottom X values -> prefix = "bottom" (before input)
  TopX = "top_x", // Top X values -> prefix = "top" (before input)

  // Stats based (see: `ColumnStats`:
  Mean = "mean", // Mean (average) value -> No input
  Median = "median", // Median value -> No input
  Mode = "mode", // Mode value -> No input
  StdDev = "stddev", // Average distance from the mean, meaning if set to -1, it will show 1 stddev below the mean.
  DomainMin = "min", // Minimum value of whole domain -> No input
  DomainMax = "max", // Maximum value of whole domain -> No input

  // Settings based:
  BaseMin = "base_min", // Minimum value from base encoding -> No input
  BaseMax = "base_max", // Maximum value from base encoding -> No input
  // ArrayLength = "array_length",
}

/**
 * Enum for condition types on scales.
 *
 * NOTE: We have other places like filters where we have conditions, but we have a different subset because
 * of the way we treat the domain for scales.
 */
export enum ScaleConditionType {
  // ------------------------------------------------------
  // Any type conditions:  (can apply to any type of value)
  Nil = "nil", // Value is not defined ('null' or 'undefined') -> symbol: "∅".
  Other = "other", // Special case where none of the prior match conditions have matched. Most useful on strings.

  // ------------------------------------------------------
  // Numeric conditions:  (although some of these can also apply lexicographically to strings)
  Between = "between", // Between two values (inclusive) -> symbol "≬". (special one)
  LT = "lt", // Less than -> symbol: "<".
  LTE = "lte", // Less than or equal to -> symbol: "≤".
  GT = "gt", // Greater than -> symbol: ">".
  GTE = "gte", // Greater than or equal to -> symbol "≥".
  EQ = "eq", // Equal to -> symbol: "=".
  InArray = "in_array", // Value is in a manually defined list -> symbol: "∈".

  // ------------------------------------------------------
  // Strings conditions:
  Contains = "contains", // String contains the search value.
  StartsWith = "starts_with", // String starts with the search value.
  EndsWith = "ends_with", // String ends with the search value.
  StringEquals = "string_equals", // String equals the search value.
  EmptyString = "empty_string", // An empty string.

  // ------------------------------------------------------
  // Date conditions:
  DateBefore = "before", // Date less than -> symbol: "<".
  DateAfter = "after", // Date greater than -> symbol: ">".
}

/**
 * Enum for the output value types of a scale.
 */
export enum ScaleOutputValueType {
  Constant = "constant", // A single constant value. Example:: "#ff0000" for a color, or 5 for a size).
  Gradient = "gradient", // A gradient between at least two values. Example for color the could be outputValues: ["#333333", "ffffff"] for a monochrome gradient).
  Cycle = "cycle", // A color cycle between at least two colors, and loops through them for each new item. Example for color: outputValues: ["#ff1100", "#22fff00", "#0033ff" ...] random nice colors).
  D3Scale = "d3_scale", // A d3 scale, such as a "linear" color ramp function. If set, then a "D3ScaleType" enum specifies what type of scale.
}

/**
 * Enum for logical operators between conditions.
 */
export enum RuleConditionOperator {
  And = "and",
  Or = "or",
}

/**
 * Enum for supported d3 scale type (https://d3js.org/d3-scale).
 *
 * This gets specified in the ScaleD3Config, when the outputType is set to "d3_scale".
 */
export enum D3ScaleType {
  Linear = "linear",
  Quantile = "quantile",
  Symlog = "symlog",
}

// ------------------------------------------------------
// Label display options:  (where ValueFirst is the most common/recommended)
export enum ScaleRuleLabelDisplay {
  ValueFirst = "value_first", // Long label, final value first... e.g. "(avg) < 42.1(50%ile)" or ">= 89.1(top 10)".
  ValueLast = "value_last", // Long label, final value last...... e.g. "(avg) < 50%ile(42.1)" or ">= top 10(89.1)".
  Short = "short", // Short label with what we think is useful... e.g. "(avg) < 42.1" or ">= top 10".
  RelativeEdit = "relative_edit", // Only the relative value..... e.g. "(avg) < 50%ile" or ">= top 10" - which potentially to edit directly & have it figure out the rule from the text.
}
